import React, { useEffect, useState } from "react";
import {
  MdAttachMoney,
  MdPendingActions,
  MdPercent,
  MdOutlinePayments,
  MdPeople
} from "react-icons/md";
import { FaMoneyBillWave } from "react-icons/fa";

import {
  getSubadminBalanceApi,
  getSubadminSellersApi
} from "../../ApiCalls/Revenue.js";

/* ---------------- helpers ---------------- */

const formatAmount = (value) =>
  `₹${Number(value ?? 0).toFixed(2)}`;

const formatDate = (dateStr) =>
  dateStr ? new Date(dateStr).toLocaleString() : "-";

/* ---------------- component ---------------- */

const SubAdminRevenue = () => {
  const [balance, setBalance] = useState(null);
  const [summary, setSummary] = useState(null);
  const [sellers, setSellers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetchRevenueData();
  }, []);

  const fetchRevenueData = async () => {
    try {
      const [balanceRes, sellersRes] = await Promise.all([
        getSubadminBalanceApi(),
        getSubadminSellersApi()
      ]);

      setBalance(balanceRes?.data?.data || null);
      setSummary(null); 
      setSellers(sellersRes?.data?.data ?? sellersRes?.data ?? []);

    } catch (error) {
      console.error("Failed to fetch subadmin revenue", error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <p className="p-6">Loading revenue data...</p>;
  }

  /* ---------------- UI components ---------------- */

  const StatCard = ({ title, value, icon: Icon, colorClass }) => (
    <div className="bg-white p-4 rounded-lg shadow-sm border flex justify-between items-center">
      <div>
        <p className="text-xs text-gray-500 uppercase font-semibold">
          {title}
        </p>
        <p className="text-xl font-bold mt-1">{value}</p>
      </div>
      <div className={`p-3 rounded-full ${colorClass}`}>
        <Icon size={24} />
      </div>
    </div>
  );

  /* ---------------- render ---------------- */

  return (
    <div className="p-6 space-y-8">

      <h1 className="text-2xl font-bold">
        Sub-Admin Revenue Dashboard
      </h1>

      {/* ================= BALANCE ================= */}
      <div className="bg-white p-6 rounded-lg shadow border-l-4 border-brand-primary">
        <h2 className="text-gray-500 text-lg">
          Total Available Balance
        </h2>

        <p className="text-4xl font-bold mt-2">
          {formatAmount(balance?.total_amount)}
        </p>

        <p className="text-sm text-gray-500 mt-2">
          Last Updated: {formatDate(balance?.last_updated)}
        </p>
      </div>

      {/* ================= SUMMARY ================= */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">

        <StatCard
          title="Lifetime Paid"
          value={formatAmount(summary?.lifetime_paid_to_subadmin)}
          icon={MdAttachMoney}
          colorClass="bg-green-100 text-green-600"
        />

        <StatCard
          title="Pending Service Fee"
          value={formatAmount(summary?.total_pending_service_fee)}
          icon={MdPendingActions}
          colorClass="bg-orange-100 text-orange-600"
        />

        <StatCard
          title="Commission %"
          value={`${summary?.commission_percentage ?? 0}%`}
          icon={MdPercent}
          colorClass="bg-blue-100 text-blue-600"
        />

        <StatCard
          title="Pending SubAdmin Comm."
          value={formatAmount(summary?.pending_subadmin_commission)}
          icon={FaMoneyBillWave}
          colorClass="bg-purple-100 text-purple-600"
        />

        <StatCard
          title="Pending Admin Share"
          value={formatAmount(summary?.pending_admin_share)}
          icon={MdOutlinePayments}
          colorClass="bg-red-100 text-red-600"
        />

      </div>

      {/* ================= SELLERS TABLE ================= */}
      <div className="bg-white rounded-lg shadow overflow-x-auto">
        <div className="px-6 py-4 border-b flex items-center gap-2">
          <MdPeople className="text-gray-600" />
          <h2 className="font-bold text-lg">
            Sellers Revenue Details
          </h2>
        </div>

        <table className="min-w-full text-sm">
          <thead className="bg-gray-100 uppercase text-xs font-bold">
            <tr>
              <th className="px-6 py-3 text-left">SI No</th>
              <th className="px-6 py-3 text-left">Name</th>
              <th className="px-6 py-3 text-left">Email</th>
              <th className="px-6 py-3 text-right">Total Earned</th>
              <th className="px-6 py-3 text-right">Service Fee</th>
              <th className="px-6 py-3 text-right">Pending Service Fee</th>
            </tr>
          </thead>

          <tbody>
            {sellers.length === 0 ? (
              <tr>
                <td colSpan="6" className="text-center py-6 text-gray-500">
                  No sellers found
                </td>
              </tr>
            ) : (
              sellers.map((seller, index) => (
                <tr key={seller.id} className="border-b hover:bg-gray-50">
                  <td className="px-6 py-3">{index + 1}</td>
                  <td className="px-6 py-3 font-medium">{seller.name}</td>
                  <td className="px-6 py-3">{seller.email}</td>
                  <td className="px-6 py-3 text-right">
                    {formatAmount(seller.total_earned)}
                  </td>
                  <td className="px-6 py-3 text-right">
                    {formatAmount(seller.service_fee_total)}
                  </td>
                  <td className="px-6 py-3 text-right text-orange-600">
                    {formatAmount(seller.service_fee_pending)}
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

    </div>
  );
};

export default SubAdminRevenue;
